import type {GeolocationFix, GpsSource} from "@/src/modules/core/types/geolocation";

/** Device GPS — preserve sub-meter precision from the Geolocation API. */
export const GPS_DEVICE_DECIMALS = 8;
/** Desktop / property fallback — legacy precision. */
export const GPS_FALLBACK_DECIMALS = 6;

function decimalsForSource(source: GpsSource | undefined): number {
  return source === "device" ? GPS_DEVICE_DECIMALS : GPS_FALLBACK_DECIMALS;
}

function formatCoordinate(value: number, decimals: number): string {
  return value.toFixed(decimals);
}

export function formatGpsCoordinates(
  latitude?: number | GeolocationFix | null,
  longitude?: number | null,
): string | null {
  if (latitude != null && typeof latitude === "object" && "longitude" in latitude) {
    const decimals = decimalsForSource(latitude.source);
    return `${formatCoordinate(latitude.latitude, decimals)},${formatCoordinate(latitude.longitude, decimals)}`;
  }

  if (latitude == null || longitude == null) {
    return null;
  }

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null;
  }

  return `${formatCoordinate(latitude, GPS_FALLBACK_DECIMALS)},${formatCoordinate(longitude, GPS_FALLBACK_DECIMALS)}`;
}

export function parseGpsCoordinatesString(
  value: string | null | undefined,
  source: GeolocationFix["source"] = "property",
): GeolocationFix | null {
  if (!value?.trim()) {
    return null;
  }

  const [latRaw, lngRaw] = value.split(",").map((part) => part.trim());
  const latitude = Number(latRaw);
  const longitude = Number(lngRaw);

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null;
  }

  return {
    latitude,
    longitude,
    accuracy: null,
    timestamp: Date.now(),
    source,
  };
}
